import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import AttractionsIcon from '@mui/icons-material/Attractions';

interface TripPricing {
  flight_cost?: number;
  accommodation_cost?: number;
  ground_transportation_cost?: number;
  meal_cost?: number;
  poi_cost?: number;
  total_cost: number;
  summary?: string;
}

interface TripPricingDialogProps {
  open: boolean;
  loading: boolean;
  pricing: TripPricing | null;
  totalTravelers?: number;
  onClose: () => void;
}

function TripPricingDialog({ open, loading, pricing, totalTravelers, onClose }: TripPricingDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AttachMoneyIcon sx={{ fontSize: 28, mr: 1, color: 'primary.main' }} />
          <Box>
            <Typography component="span" variant="h6" fontWeight="bold">
              Trip Pricing Breakdown
            </Typography>
            {totalTravelers > 0 && (
              <Typography variant="body2" color="text.secondary">
                Estimated for {totalTravelers} {totalTravelers === 1 ? 'traveler' : 'travelers'}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : pricing ? (
          <Box>
            {/* Pricing Breakdown */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {pricing.flight_cost > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FlightIcon color="action" />
                    <Typography variant="body1">Flight</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium" sx={{ minWidth: 80 }}>
                    ${pricing.flight_cost.toLocaleString()}
                  </Typography>
                </Box>
              )}

              {pricing.accommodation_cost > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HotelIcon color="action" />
                    <Typography variant="body1">Accommodation</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium" sx={{ minWidth: 80 }}>
                    ${pricing.accommodation_cost.toLocaleString()}
                  </Typography>
                </Box>
              )}

              {pricing.ground_transportation_cost > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DirectionsCarIcon color="action" />
                    <Typography variant="body1">Ground Transportation</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium" sx={{ minWidth: 80 }}>
                    ${pricing.ground_transportation_cost.toLocaleString()}
                  </Typography>
                </Box>
              )}

              {pricing.meal_cost > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FastfoodIcon color="action" />
                    <Typography variant="body1">Meals</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium" sx={{ minWidth: 80 }}>
                    ${pricing.meal_cost.toLocaleString()}
                  </Typography>
                </Box>
              )}

              {pricing.poi_cost > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttractionsIcon color="action" />
                    <Typography variant="body1">Attractions & Activities</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium" sx={{ minWidth: 80 }}>
                    ${pricing.poi_cost.toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Total Cost */}
            <Box
              sx={{
                borderTop: 2,
                borderColor: 'divider',
                pt: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 4
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Total Estimated Cost
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary.main" sx={{ minWidth: 80 }}>
                ${pricing.total_cost.toLocaleString()}
              </Typography>
            </Box>

            {/* Summary */}
            {pricing.summary && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {pricing.summary}
                </Typography>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              * Prices are estimates and may vary based on availability and booking time
            </Typography>
          </Box>
        ) : (
          <Typography color="text.secondary">
            No pricing information available for this trip.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TripPricingDialog;
